<template>
  <SlideIn :open="openFilters" heading="Filters" @close="openFilters = false">
    <template #content>
      <SelectedFilters
        :selected-filters="filters"
        @clearFilter="handleClearFilter"
        @resetFilters="handleResetClick"
      />
      <div v-for="filter in sortedAvailableFilters" :key="filter.id">
        <Accordion show-arrow class="filter-accordion">
          <template #header>
            {{ filter.label }}
            <template v-if="shouldShowFilterCount(filter)">
              ({{ filter.values.length }})
            </template>
          </template>
          <template #body>
            <component
              :is="getFilterName(filter.type)"
              :values="filter.values"
              :filters="filters"
              @change="handleFilterChange"
            />
          </template>
        </Accordion>
      </div>
      <!-- <SortFilter @change="handleSortChange" /> -->
    </template>
    <template #footer>
      <div class="button-group">
        <Button full-width @click="applyFilter">Apply filters</Button>
        <Button theme="secondary" full-width @click="handleResetClick">
          Reset
        </Button>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import camelCase from "lodash-es/camelCase";
import cloneDeep from "lodash-es/cloneDeep";
import PriceFilter from "@/components/collection/filters/priceFilter.vue";
import ListFilter from "@/components/collection/filters/listFilter.vue";
import SortFilter from "@/components/collection/filters/sortFilter.vue";
import SelectedFilters from "@/components/collection/SelectedFilters.vue";
import Accordion from "@/components/Accordion.vue";
import Button from "@/components/Button.vue";
import { mapState, mapActions } from "vuex";
import isEqual from "lodash-es/isEqual";
import SlideIn from "@/components/SlideIn.vue";

const filterTypes = {
  LIST: "list",
  PRICE_RANGE: "price",
};

export default {
  name: "Filters",
  components: {
    PriceFilter,
    ListFilter,
    SortFilter,
    Accordion,
    SlideIn,
    Button,
    SelectedFilters,
  },
  props: {
    availableFilters: {
      type: Array,
      default: () => [],
    },
    selectedFilters: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["filterChange", "sortChange"],
  data() {
    return {
      filters: [],
    };
  },
  computed: {
    ...mapState("collection", ["isFiltersOpen"]),
    openFilters: {
      get() {
        return this.isFiltersOpen;
      },
      set(value) {
        this.setIsFiltersOpen(value);
      },
    },
    sortedAvailableFilters() {
      const filters = [];
      const sizeOrder = ["XXS", "XS", "S", "M", "L", "XL", "XXL"];

      this.availableFilters.forEach((filter) => {
        if (filter.id === "filter.v.option.size") {
          const clonedFilter = cloneDeep(filter);
          clonedFilter.values.sort((a, b) => {
            return sizeOrder.indexOf(a.label) - sizeOrder.indexOf(b.label);
          });
          return filters.push(clonedFilter);
        }

        filters.push(filter);
      });

      const filtersWithMoreThanOneOption = filters.filter((filter) => {
        const excludesIds = ["filter.v.price"];
        const valuesGreaterThanOne = filter.values.length > 1;

        if (excludesIds.includes(filter.id)) {
          return true;
        }

        if (filter.id === "filter.v.availability") {
          return false;
        }

        if (valuesGreaterThanOne) {
          return true;
        }

        return false;
      });

      return filtersWithMoreThanOneOption;
    },
  },
  watch: {
    selectedFilters() {
      this.filters = this.selectedFilters;
    },
  },
  methods: {
    ...mapActions("collection", ["setIsFiltersOpen", "setFilters"]),
    getFilterName(type) {
      const filterName = camelCase(filterTypes[type]);
      return filterName + "Filter";
    },
    handleSortChange(sort) {
      this.$emit("sortChange", sort);
    },
    handleFilterChange(input) {
      const selectedFilters = cloneDeep(this.filters) || [];
      const filterKey = Object.keys(input)[0];

      const filterIsSelected = selectedFilters.some((selectedFilter) =>
        isEqual(selectedFilter[filterKey], input[filterKey])
      );

      if (filterIsSelected || filterKey === "price") {
        const index = this.filters.findIndex((filter) => {
          return Object.keys(filter)[0] === Object.keys(input)[0];
        });

        if (index === -1) {
          this.filters.push(input);
        } else if (filterKey === "price") {
          this.filters[index] = input;
        } else {
          this.filters.splice(index, 1);
        }

        const containsSizeValue = this.filters.some(
          (item) => item.variantOption?.name === "size"
        );

        if (!containsSizeValue) {
          console.log("Does not contain size value");

          const indexToRemove = this.filters.findIndex((item) =>
            Object.prototype.hasOwnProperty.call(item, "available")
          );

          if (indexToRemove !== -1) {
            this.filters.splice(indexToRemove, 1);
          }
        }
      } else {
        if (filterKey === "variantOption" && input[filterKey].name === "size") {
          const containsAvailability = this.filters.some((item) =>
            Object.prototype.hasOwnProperty.call(item, "available")
          );

          if (!containsAvailability) {
            this.filters.push({ available: true });
          }
        }

        this.filters.push(input);
      }
    },
    applyFilter() {
      this.setIsFiltersOpen(false);
      this.$emit("filterChange", this.filters);
    },
    handleClearFilter(item) {
      const selectedFilters = cloneDeep(this.filters) || [];
      const filterKey = Object.keys(item)[0];

      const filterIsSelected = selectedFilters?.some((selectedFilter) => {
        if (isEqual(selectedFilter[filterKey], item[filterKey])) {
          return true;
        }
        return false;
      });

      if (filterIsSelected || filterKey == "price") {
        const index = this.filters.findIndex((filter) => isEqual(filter, item));

        this.filters.splice(index, 1);
      }
    },
    handleResetClick() {
      this.filters = [];
      this.setIsFiltersOpen(false);
      this.$emit("filterChange", this.filters);
    },
    shouldShowFilterCount(filter) {
      const hiddenFilterCountIds = ["filter.v.availability", "filter.v.price"];
      if (hiddenFilterCountIds.includes(filter.id)) {
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  gap: 10px;
}

::v-deep() {
  .filter-accordion.accordion--open {
    transition-timing-function: ease-out;
    background-color: $color-light;
    padding-bottom: 10px;
    margin-bottom: 20px;

    .accordion__header {
      transition-timing-function: ease-out;
      padding-top: 20px;
    }
  }
}

.filter-accordion {
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
  transition: all 0.3s;
  transition-timing-function: ease-in;

  ::v-deep() {
    .accordion__header {
      transition: padding-top 0.3s;
      transition-timing-function: ease-in;
      align-items: center;
      font-family: $body-font-family;
      text-transform: uppercase;
      width: 100%;
      font-size: 9px;
      background: none;
      outline: none;
      border: none;
      padding: 8px 0;
      font-weight: 600;
    }
  }
}
</style>
